/**
 * The arithmetic behind both payroll screens.
 *
 * The book splits payroll in two and is explicit about why: "The dashboard spans
 * every run, so it cannot show progress; progress belongs to a single run."
 * Neither screen gets a stage timeline. A run has one status, and the audit
 * trail says how it got there.
 *
 * The rule that most needs to live in one place is the variance threshold. The
 * book: "A variance chip appears only where net pay moved 30% or more against
 * last cycle — the same threshold as the warning above it — so a chip in the
 * register and a count in the warning can never disagree." Two constants would
 * eventually drift; there is one.
 */

import { MONTHS, periodLabel, money, RUN_STATUS_LABEL, RUN_STATUS_TONE } from "./payrollUtils";

/**
 * Labels, statuses and the general money format already live in `payrollUtils`,
 * which seventeen files import. They are re-exported here so a screen needs one
 * import, NOT redefined — a second status-tone map would drift from the first,
 * which is exactly the defect this module was written to avoid elsewhere.
 */
export { periodLabel, money, RUN_STATUS_LABEL, RUN_STATUS_TONE };

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "Mar 2026", for a chart axis where the full month will not fit. */
export const periodShort = (month, year) => {
  const i = Number(month);
  if (!Number.isInteger(i) || i < 0 || i > 11 || !year) return "—";
  return `${MONTHS_SHORT[i]} ${year}`;
};

/** The one threshold, read by both the warning and the chip. */
export const VARIANCE_THRESHOLD = 0.3;

/** "31 March" — spelled from a table; ICU renders "Sept" for September. */
export const dayMonth = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  let mo, d;
  if (m) {
    [, , mo, d] = m;
  } else {
    const parsed = new Date(s);
    if (Number.isNaN(parsed.getTime())) return null;
    mo = parsed.getUTCMonth() + 1;
    d = parsed.getUTCDate();
  }
  const i = Number(mo) - 1;
  if (i < 0 || i > 11) return null;
  return `${Number(d)} ${MONTHS[i]}`;
};

/**
 * Money to the cent.
 *
 * `payrollUtils.money` rounds, which is right for a dashboard headline. The
 * register and its footer are the thing being signed off, so they state cents —
 * a total that does not add up to the penny is the one error nobody forgives.
 */
export const moneyExact = (n, symbol = "₹") =>
  `${symbol}${(Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** A run nobody has finished with. These are the ones a dashboard chases. */
export const isOpen = (run) => run?.status !== "paid" && run?.status !== "rejected";
export const isComplete = (run) => run?.status === "paid";

/**
 * The open run: the most recent one still in flight. Sorted by period rather
 * than by creation, because an off-cycle run created later can belong to an
 * earlier month.
 */
export const byPeriodDesc = (a, b) =>
  (b.year - a.year) || (b.month - a.month);

export const openRun = (runs = []) =>
  [...runs].filter(isOpen).sort(byPeriodDesc)[0] || null;

export const completedRuns = (runs = []) =>
  [...runs].filter(isComplete).sort(byPeriodDesc);

/**
 * Variance is the SERVER's, not ours.
 *
 * `getById` already compares each line against the prior run and returns
 * `item.variance = { flag, prevNet, deltaPct }` plus
 * `varianceMeta = { flaggedCount, spikePct, comparedTo }`, using its own
 * `SPIKE_PCT = 30`. Recomputing it here would be a second implementation of one
 * rule, and the book's whole point about this threshold is that "a chip in the
 * register and a count in the warning can never disagree" — which is only
 * guaranteed if there is one of them. The server's is also the one that decides
 * what gets stored, so it wins.
 */
export const varianceOf = (item) => {
  const v = item?.variance;
  if (!v || !v.prevNet) return null;
  const pct = Number(v.deltaPct);
  if (Number.isNaN(pct)) return null;
  return { pct, direction: pct >= 0 ? "up" : "down", significant: v.flag === "spike" };
};

export const hasNoBank = (item) => item?.bankDetailsOnFile === false;

/** The server sets `negativeNet`; the arithmetic is the fallback for old rows. */
export const isNegativeNet = (item) =>
  item?.negativeNet === true || (Number(item?.netPay) || 0) < 0;

/**
 * The warnings above the register, in the order the book stacks them:
 * "review first, then exclusions, then arithmetic that cannot stand."
 *
 * Counts come from the run the server returned — the same object the register's
 * chips read — so a warning and the rows beneath it cannot disagree.
 */
export const runWarnings = (run) => {
  const items = run?.items || [];
  const moved = run?.varianceMeta?.flaggedCount || 0;
  const threshold = run?.varianceMeta?.spikePct ?? Math.round(VARIANCE_THRESHOLD * 100);
  const noBank = run?.totals?.missingBankDetails ?? items.filter(hasNoBank).length;
  const negative = items.filter(isNegativeNet).length;

  const out = [];
  if (moved > 0) {
    out.push({
      key: "variance",
      tone: "warning",
      lead: `${moved} ${moved === 1 ? "person has" : "people have"} a net-pay change of ${threshold}% or more`,
      rest: "against last cycle. Review before approving.",
    });
  }
  if (noBank > 0) {
    out.push({
      // Danger, not warning: this one silently removes people from the payment
      // file. The book tints it the same as the arithmetic failure below it.
      key: "noBank",
      tone: "danger",
      lead: `${noBank} ${noBank === 1 ? "person has" : "people have"} no bank details on file.`,
      rest: "They will be excluded from this run unless the details are added before it is paid.",
    });
  }
  if (negative > 0) {
    out.push({
      key: "negative",
      tone: "danger",
      lead: `${negative} ${negative === 1 ? "person has" : "people have"} a negative net pay`,
      rest: "— deductions exceed pay. Reduce the recovery or carry it to a later cycle before approving.",
    });
  }
  return out;
};

/** A line's own state, which is what its badge says. */
export const itemState = (item) => {
  if (isNegativeNet(item)) return { key: "negative", label: "Negative", tone: "danger" };
  if (hasNoBank(item)) return { key: "noBank", label: "No bank", tone: "danger" };
  if (Number(item?.additions) > 0 || (item?.reimbursementTotal || 0) > 0)
    return { key: "review", label: "Needs approval", tone: "warning" };
  return { key: "ready", label: "Ready", tone: "success" };
};

const sumBy = (rows, pick) => rows.reduce((t, r) => t + (Number(pick(r)) || 0), 0);

/**
 * The footer row. "A register that does not add up is the thing being signed
 * off", so every numeric column is summed — including LOP days, which is a
 * count rather than money and still has to reconcile.
 */
export const columnTotals = (items = []) => ({
  people: items.length,
  gross: sumBy(items, (i) => i.grossEarnings ?? i.gross),
  additions: sumBy(items, (i) => i.additions),
  deductions: sumBy(items, (i) => i.totalDeductions ?? i.deductions),
  lop: sumBy(items, (i) => i.lopDays),
  net: sumBy(items, (i) => i.netPay),
});

/**
 * The five totals at the head of a run, and the equation under them.
 *
 * The book: "gross minus deductions equalling net is the one arithmetic anyone
 * checks by hand, and employer cost sits outside it rather than inside." So the
 * equation is stated, and employer cost is appended after it rather than folded
 * into it.
 *
 * Prefers the run's own snapshotted totals — the run records what was true when
 * it was built — and falls back to summing the lines.
 */
export const runTotals = (run) => {
  const t = run?.totals || {};
  const summed = columnTotals(run?.items || []);
  const gross = t.totalGross ?? summed.gross;
  const deductions = t.totalDeductions ?? summed.deductions;
  const net = t.totalNet ?? summed.net;
  return {
    people: t.employeeCount ?? summed.people,
    gross,
    deductions,
    net,
    employer: t.totalEmployer ?? 0,
    // Whether the stated totals actually reconcile. A run whose own numbers do
    // not add up is worth saying out loud rather than rendering as if fine.
    balances: Math.abs(gross - deductions - net) < 0.01,
  };
};

export const equationLine = (totals, symbol = "₹") =>
  `${moneyExact(totals.gross, symbol)} gross − ${moneyExact(totals.deductions, symbol)} deductions = ${moneyExact(
    totals.net,
    symbol
  )} net · employer cost ${moneyExact(totals.employer, symbol)}, on top of gross`;

/**
 * The dashboard's counters.
 *
 * "who cannot be paid, what the open run comes to, and what the last few came
 * to" — the three questions the book says this screen answers.
 */
export const dashboardStats = (runs = [], { roster = [], structuredIds = [], legalUnits = 0 } = {}) => {
  const open = openRun(runs);
  const done = completedRuns(runs);
  const last = done[0] || null;

  const structured = new Set(structuredIds.map(String));
  const active = roster.filter((e) => e.isActive !== false);
  const unstructured = active.filter((e) => !structured.has(String(e._id)));

  return {
    onPayroll: { count: active.length, legalUnits },
    noStructure: { count: unstructured.length, people: unstructured },
    pendingApproval: {
      count: open?.status === "pending_approval" ? open.totals?.employeeCount || 0 : 0,
      period: open ? periodLabel(open.month, open.year) : null,
      isPending: open?.status === "pending_approval",
    },
    lastPayout: last
      ? { amount: last.totals?.totalNet || 0, period: periodLabel(last.month, last.year), people: last.totals?.employeeCount || 0 }
      : null,
    // Employer-side statutory cost of the most recent completed run. There is
    // no filing-due date anywhere in the data, so the counter states the period
    // it covers instead of inventing a deadline.
    statutory: last ? { amount: last.totals?.totalEmployer || 0, period: periodLabel(last.month, last.year) } : null,
    open,
  };
};

/** The net-payout trend: completed runs only, oldest first. */
export const netTrend = (runs = [], count = 5) => {
  const done = completedRuns(runs).slice(0, count).reverse();
  return done.map((r) => ({
    key: `${r.year}-${r.month}`,
    label: periodShort(r.month, r.year),
    amount: r.totals?.totalNet || 0,
  }));
};

export const trendChange = (points = []) => {
  if (points.length < 2) return null;
  const first = points[0].amount;
  const last = points[points.length - 1].amount;
  if (!first) return null;
  return { change: (last - first) / first, from: points[0].label };
};

/**
 * Who cannot be paid, gathered from both causes into one list, because the
 * reader's question is "what do I have to fix", not "what kind of problem is
 * it". A person missing both appears once, named by whichever blocks first.
 */
export const needsAttention = (open, unstructured = []) => {
  const rows = [];
  const seen = new Set();
  unstructured.forEach((e) => {
    const id = String(e._id);
    if (seen.has(id)) return;
    seen.add(id);
    rows.push({ employee: e, reason: "No salary structure", tone: "danger" });
  });
  (open?.items || []).filter(hasNoBank).forEach((it) => {
    const e = it.employee;
    const id = String(e?._id || e || "");
    if (!id || seen.has(id)) return;
    seen.add(id);
    rows.push({ employee: e, reason: "No bank details", tone: "warning" });
  });
  return rows;
};

/** One line of the audit trail. */
export const auditLine = (log) => {
  const at = log?.createdAt ? new Date(log.createdAt) : null;
  const when = at && !Number.isNaN(at.getTime())
    ? `${dayMonth(log.createdAt)} at ${String(at.getUTCHours()).padStart(2, "0")}:${String(at.getUTCMinutes()).padStart(2, "0")}`
    : null;
  const actor = log?.actor
    ? `${log.actor.fname || ""} ${log.actor.lname || ""}`.trim() || "automatic"
    : "automatic";
  return {
    message: log?.message || log?.action || "—",
    // The book puts a name and a timestamp against every step; where the system
    // did it, "automatic" is the honest name rather than a blank.
    meta: [when, actor].filter(Boolean).join(" · "),
    reason: log?.reason || null,
  };
};
